/**
 * slotAvailabilityUtils.js
 * ─────────────────────────────────────────────────────────────────────────────
 * Classificação de um horário do seletor de horários (CreateAppointmentPicker
 * e ReschedulePanel): por que ele não pode ser escolhido e com que cor aparece.
 *
 * Status possíveis:
 *   - 'past'      hora que já passou (cinza sólido)
 *   - 'blocked'   fora do expediente ou intervalo bloqueado na agenda (listrado)
 *   - 'occupied'  já tem agendamento (vermelho)
 *   - 'warning'   fora do horário próprio do profissional ou no limite dele
 *                 (âmbar): pode ser escolhido, a tela só avisa
 *
 * A ordem espelha o SlotAvailabilityService do backend: passado, fim do
 * expediente, intervalo bloqueado e, só no fim, agendamento existente.
 *
 * @module slotAvailabilityUtils
 */

import { isPast, isToday } from './calendarUtils';

export const SLOT_REASON_OUTSIDE = 'Fora do expediente';
export const SLOT_REASON_PAST    = 'Horário já passou';
export const SLOT_REASON_BREAK   = 'Intervalo bloqueado na agenda (ex.: almoço)';
export const SLOT_REASON_BOOKED  = 'Já tem agendamento neste horário';
export const SLOT_REASON_PROFESSIONAL_OFF   = 'Fora do horário deste profissional';
export const SLOT_REASON_PROFESSIONAL_LIMIT = 'Profissional já chegou no máximo de atendimentos';

/** Status que o seletor deixa escolher. */
export const isSlotSelectable = (slot) => slot?.status === 'available' || slot?.status === 'warning';

/**
 * Horário barrado só pelas regras do profissional (API devolve `reason`):
 * vira aviso em vez de bloqueio. Null quando não é o caso.
 *
 * @param {{available: boolean, reason?: string}} apiSlot
 * @returns {{status: 'warning', reason: string}|null}
 */
export function professionalWarning(apiSlot) {
  if (!apiSlot || apiSlot.available) return null;
  if (apiSlot.reason === 'off_hours') return { status: 'warning', reason: SLOT_REASON_PROFESSIONAL_OFF };
  if (apiSlot.reason === 'limit') return { status: 'warning', reason: SLOT_REASON_PROFESSIONAL_LIMIT };
  return null;
}

/**
 * Soma à grade da tela os horários que só a API tem (intervalo próprio do
 * profissional, ex.: 08:30 numa clínica de hora em hora), em ordem.
 *
 * @param {Array<{hour: number, minute: number, label: string}>} slots
 * @param {Array<{time: string}>|null} apiSlots
 * @returns {Array<{hour: number, minute: number, label: string}>}
 */
export function withApiSlotTimes(slots, apiSlots) {
  if (!apiSlots?.length) return slots;
  const labels = new Set(slots.map((slot) => slot.label));
  const extra = apiSlots
    .filter((apiSlot) => /^\d{2}:\d{2}$/.test(apiSlot.time) && !labels.has(apiSlot.time))
    .map((apiSlot) => {
      const [hour, minute] = apiSlot.time.split(':').map(Number);
      return { hour, minute, label: apiSlot.time };
    });
  if (!extra.length) return slots;
  return [...slots, ...extra].sort((a, b) => a.hour * 60 + a.minute - (b.hour * 60 + b.minute));
}

/**
 * Verifica se um horário (dia + hora:minuto) já passou.
 *
 * @param {Date} date
 * @param {number} hour
 * @param {number} minute
 * @returns {boolean}
 */
export function isSlotPast(date, hour, minute) {
  if (isPast(date)) return true;
  if (!isToday(date)) return false;
  const now = new Date();
  return hour * 60 + minute <= now.getHours() * 60 + now.getMinutes();
}

/**
 * Classifica um horário que a API marcou como indisponível.
 *
 * @param {{hour: number, minute: number}} slot
 * @param {Object} ctx
 * @param {Date} ctx.date - Dia do horário
 * @param {number} ctx.intervalMinutes - Intervalo entre slots
 * @param {number|null} ctx.endHour - Hora de fim do expediente (CompanyConfig.AvailableTime[1])
 * @param {Array} ctx.blockedTimes - Horas bloqueadas da empresa (CompanyConfig.BlockedTimes)
 * @returns {{status: 'past'|'blocked'|'occupied', reason: string}}
 */
export function classifyUnavailableSlot(slot, { date, intervalMinutes, endHour, blockedTimes }) {
  const start = slot.hour * 60 + slot.minute;
  const end = start + intervalMinutes;

  if (isSlotPast(date, slot.hour, slot.minute)) {
    return { status: 'past', reason: SLOT_REASON_PAST };
  }
  if (endHour != null && end > endHour * 60) {
    return { status: 'blocked', reason: SLOT_REASON_OUTSIDE };
  }
  const hitsBreak = (blockedTimes || []).some((bt) => {
    const value = Number(bt);
    if (Number.isNaN(value)) return false;
    const btStart = Math.round(value * 60);
    const btEnd = btStart + intervalMinutes;
    return Math.floor(value) === slot.hour || (btStart < end && btEnd > start);
  });
  if (hitsBreak) return { status: 'blocked', reason: SLOT_REASON_BREAK };

  return { status: 'occupied', reason: SLOT_REASON_BOOKED };
}
